import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, ChevronRight, Headphones, KeyRound, LogOut, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { UserPublic } from '@underline/shared';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { EmptyState } from '@/components/ui/empty-state';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { UserAvatar } from '@/components/users/user-avatar';
import { MeFormModal } from '@/components/users/me-form-modal';
import { ChangePasswordModal } from '@/components/users/change-password-modal';
import { UserFormModal } from '@/components/users/user-form-modal';
import { useAuthStore } from '@/stores/auth-store';
import { useLogout } from '@/hooks/use-auth';
import { useDeleteUser, useUploadMyAvatar, useUploadUserAvatar, useUsers } from '@/hooks/use-users';
import { useAttractions } from '@/hooks/use-attractions';
import { useNotifications } from '@/hooks/use-notifications';
import { TypedHeading } from '@/components/ui/typed-heading';

declare const __APP_VERSION__: string;
declare const __COMMIT_HASH__: string;

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();

  const canManage = user?.role === 'admin' || user?.role === 'gerente' || user?.role === 'gestor';
  const isAdmin = user?.role === 'admin';

  const uploadMyAvatar = useUploadMyAvatar();
  const uploadUserAvatar = useUploadUserAvatar();

  // Estado real de push (subscribe/unsubscribe) ligado ao Switch
  const { state: notif, subscribe, unsubscribe } = useNotifications();

  function handleAvatarFile(file: File, userId?: number) {
    if (!file.type.startsWith('image/')) { toast.error('Selecione uma imagem'); return; }
    if (userId) {
      uploadUserAvatar.mutate({ id: userId, file }, { onSuccess: () => toast.success('Foto atualizada'), onError: () => toast.error('Erro ao enviar foto') });
    } else {
      uploadMyAvatar.mutate(file, { onSuccess: () => toast.success('Foto atualizada'), onError: () => toast.error('Erro ao enviar foto') });
    }
  }

  const [meOpen, setMeOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserPublic | null>(null);
  const { data: users = [] } = useUsers();
  const { data: attractions = [] } = useAttractions();
  const delUser = useDeleteUser();

  function openCreateUser() {
    setEditUser(null);
    setUserFormOpen(true);
  }

  function openEditUser(u: UserPublic) {
    setEditUser(u);
    setUserFormOpen(true);
  }

  function handleDeleteUser(u: UserPublic) {
    if (u.id === user?.id) return;
    if (!confirm(`Desativar ${u.name}?`)) return;
    delUser.mutate(u.id, {
      onSuccess: () => toast.success(`${u.name} desativado`),
      onError: (err) => toast.error(err.message),
    });
  }

  function handleLogout() {
    setLogoutOpen(false);
    logout.mutate(undefined, { onSuccess: () => navigate('/login') });
  }

  // Liga/desliga push de fato (subscribe ↔ unsubscribe) conforme o Switch
  function handleToggleNotif(next: boolean) {
    if (next) {
      subscribe()
        .then(() => toast.success('Notificações ativadas'))
        .catch((err: unknown) =>
          toast.error(err instanceof Error ? err.message : 'Não foi possível ativar'),
        );
    } else {
      unsubscribe()
        .then(() => toast.success('Notificações desativadas'))
        .catch(() => toast.error('Não foi possível desativar'));
    }
  }

  if (!user) return null;

  return (
    <div>
      {/* Cabeçalho */}
      <header style={{ marginBottom: 20, animation: 'u-rise 0.4s var(--ease-underline) 0s both' }}>
        <TypedHeading text="Ajustes" />
      </header>

      {/* Cartão de perfil */}
      <section
        style={{ animation: 'u-rise 0.4s var(--ease-underline) 0.06s both' }}
        className="mb-6 flex items-center gap-4 rounded-2xl border border-border bg-card p-[18px] shadow-soft"
      >
        <label className="group relative shrink-0 cursor-pointer" title="Trocar foto">
          <UserAvatar
            name={user.name}
            colorHex={user.color_hex}
            url={user.avatar_url}
            size="lg"
          />
          <span className="absolute inset-0 grid place-items-center rounded-full bg-black/40 opacity-0 transition group-hover:opacity-100">
            <Pencil className="h-4 w-4 text-white" />
          </span>
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={uploadMyAvatar.isPending}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAvatarFile(f); e.target.value = ''; }}
          />
        </label>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-[17px] font-bold tracking-[-0.01em] text-foreground">
            {user.name}
          </p>
          <p className="mt-0.5 truncate text-[13px] text-muted-foreground">{user.email}</p>
          <span className="mt-2 inline-block">
            <Badge variant="prox">{user.role}</Badge>
          </span>
        </div>
        <button
          type="button"
          onClick={() => setMeOpen(true)}
          aria-label="Editar perfil"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-surface-2 text-muted-foreground transition hover:text-foreground"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </section>

      {/* Preferências */}
      <div style={{ animation: 'u-rise 0.4s var(--ease-underline) 0.12s both' }}>
        <Eyebrow className="mb-3">preferências</Eyebrow>
        <div className="mb-6 overflow-hidden rounded-2xl border border-border bg-card">
          {/* Notificações push — Switch ligado ao estado real de push */}
          <div className="flex items-center gap-3.5 px-4 py-3.5">
            <span className="inline-flex text-muted-foreground">
              <Bell className="h-[17px] w-[17px]" />
            </span>
            <span className="flex-1 text-sm font-medium text-foreground">Notificações push</span>
            <Switch
              checked={notif.subscribed}
              disabled={!notif.supported}
              onCheckedChange={handleToggleNotif}
              aria-label="Notificações push"
            />
          </div>
          {/* Trocar senha */}
          <button
            type="button"
            onClick={() => setPwOpen(true)}
            className="flex w-full items-center gap-3.5 border-t border-border px-4 py-3.5 text-left transition hover:bg-muted"
          >
            <span className="inline-flex text-muted-foreground">
              <KeyRound className="h-[17px] w-[17px]" />
            </span>
            <span className="flex-1 text-sm font-medium text-foreground">Trocar senha</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
          {/* DJs / Atrações — gestor/admin */}
          {canManage && (
            <button
              type="button"
              onClick={() => navigate('/settings/djs')}
              className="flex w-full items-center gap-3.5 border-t border-border px-4 py-3.5 text-left transition hover:bg-muted"
            >
              <span className="inline-flex text-muted-foreground">
                <Headphones className="h-[17px] w-[17px]" />
              </span>
              <span className="flex-1 text-sm font-medium text-foreground">DJs / Atrações</span>
              <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                {attractions.length}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Equipe */}
      <div style={{ animation: 'u-rise 0.4s var(--ease-underline) 0.18s both' }}>
        <div className="mb-3 flex items-baseline justify-between">
          <Eyebrow>equipe</Eyebrow>
          <div className="flex items-center gap-3">
            {canManage && (
              <button
                type="button"
                onClick={openCreateUser}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary"
              >
                <Plus className="h-3 w-3" /> Novo
              </button>
            )}
            <span className="font-mono text-[11px] text-muted-foreground">{users.length}</span>
          </div>
        </div>
        <div className="mb-7 overflow-hidden rounded-2xl border border-border bg-card">
          {users.length === 0 ? (
            <EmptyState title="Nenhum usuário" />
          ) : (
            users.map((u, i) => (
              <div
                key={u.id}
                className="flex items-center gap-3.5 px-4 py-3.5"
                style={i ? { borderTop: '1px solid var(--border)' } : undefined}
              >
                <label className="group relative shrink-0 cursor-pointer" title="Trocar foto">
                  <UserAvatar
                    name={u.name}
                    colorHex={u.color_hex}
                    url={u.avatar_url}
                    size="sm"
                  />
                  {canManage && (
                    <>
                      <span className="absolute inset-0 grid place-items-center rounded-full bg-black/40 opacity-0 transition group-hover:opacity-100">
                        <Pencil className="h-3 w-3 text-white" />
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        disabled={uploadUserAvatar.isPending}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAvatarFile(f, u.id); e.target.value = ''; }}
                      />
                    </>
                  )}
                </label>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">
                    {u.name}{' '}
                    {u.id === user.id && (
                      <span className="font-normal text-muted-foreground">(você)</span>
                    )}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">{u.email}</span>
                </span>
                <Badge variant="muted">{u.role.replace('_', ' ')}</Badge>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openEditUser(u)}
                      aria-label="Editar"
                      className="grid h-8 w-8 place-items-center rounded-md hover:bg-muted"
                    >
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                    {isAdmin && u.id !== user.id && (
                      <button
                        type="button"
                        onClick={() => handleDeleteUser(u)}
                        aria-label="Excluir"
                        className="grid h-8 w-8 place-items-center rounded-md text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Sair + rodapé */}
      <div style={{ animation: 'u-rise 0.4s var(--ease-underline) 0.24s both' }}>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => setLogoutOpen(true)}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium text-destructive transition active:scale-[0.97]"
        >
          <LogOut className="h-[15px] w-[15px]" /> Sair da conta
        </motion.button>
        <p className="mt-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
          v{__APP_VERSION__} · {__COMMIT_HASH__} · underline_
        </p>
      </div>

      {/* Modais */}
      <MeFormModal open={meOpen} onClose={() => setMeOpen(false)} />
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      <UserFormModal
        open={userFormOpen}
        user={editUser}
        onClose={() => {
          setUserFormOpen(false);
          setEditUser(null);
        }}
      />
      <BottomSheet open={logoutOpen} onClose={() => setLogoutOpen(false)} title="Sair da conta?">
        <p className="mb-4 text-sm text-muted-foreground">
          Você precisará entrar novamente para acessar o painel.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setLogoutOpen(false)}
            className="flex-1 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium hover:bg-muted"
          >
            Cancelar
          </button>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleLogout}
            className="flex-1 rounded-lg bg-destructive px-4 py-3 text-sm font-semibold text-destructive-foreground"
          >
            Sair
          </motion.button>
        </div>
      </BottomSheet>
    </div>
  );
}
